import { supabase } from '../../supabaseClient';
import {
  ENTRY_TYPE,
  INSTALLMENT_STATUS,
  isAccountPlanCompatible,
} from '../constants/financial';
import { formatLocalISO, MONTHS_SHORT } from './financialPeriod';
import { fetchAllRows } from './supabaseFetchAll';
import { loadBreakdownByEntries } from './breakdown';
import { loadAccountPlans } from './accountPlan';

// Soma de parcelas pagas (regime de caixa) agrupadas por entry type dentro
// de um range. Filtro pelo payment_date — só conta o que efetivamente entrou
// ou saiu do caixa, não o que foi apenas faturado.
export const loadPeriodSummary = async (companyId, range) => {
  if (!range?.from || !range?.to) return { receitas: 0, despesas: 0 };

  const { data, error } = await supabase
    .from('Installment')
    .select('value, financial_entry:FinancialEntry!inner(type, company_id, deleted_at)')
    .eq('financial_entry.company_id', companyId)
    .eq('status', INSTALLMENT_STATUS.PAGO)
    .gte('payment_date', range.from)
    .lte('payment_date', range.to)
    .is('deleted_at', null)
    .is('financial_entry.deleted_at', null);

  if (error || !data) return { receitas: 0, despesas: 0 };

  const receitas = data
    .filter((i) => i.financial_entry?.type === ENTRY_TYPE.RECEITA)
    .reduce((sum, i) => sum + (Number(i.value) || 0), 0);
  const despesas = data
    .filter((i) => i.financial_entry?.type === ENTRY_TYPE.DESPESA)
    .reduce((sum, i) => sum + (Number(i.value) || 0), 0);

  return { receitas, despesas };
};

// Bucketiza lançamentos por mês de EMISSÃO (issue_date), em regime de
// competência — MESMA base dos cards e da DRE. Receitas = plano tipo 'receita';
// Despesas = dedução + custo + despesa. Função pura (recebe a data de
// referência) para ser testável sem depender do relógio.
export const bucketEntriesByMonth = (entries, referenceDate, monthsCount = 6) => {
  const buckets = [];
  for (let i = monthsCount - 1; i >= 0; i--) {
    const d = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - i, 1);
    buckets.push({
      mes: MONTHS_SHORT[d.getMonth()],
      year: d.getFullYear(),
      month: d.getMonth(),
      receitas: 0,
      despesas: 0,
    });
  }

  entries.forEach((entry) => {
    if (!entry.issue_date) return;
    const d = new Date(entry.issue_date + 'T00:00:00');
    const bucket = buckets.find((b) => b.year === d.getFullYear() && b.month === d.getMonth());
    if (!bucket) return;
    const type = entry.AccountPlan?.type;
    const value = Number(entry.total_value) || 0;
    if (type === 'receita') bucket.receitas += value;
    else if (isAccountPlanCompatible(type, ENTRY_TYPE.DESPESA)) bucket.despesas += value;
  });

  return buckets.map(({ mes, receitas, despesas }) => ({ mes, receitas, despesas }));
};

// Receitas x Despesas dos últimos N meses para o gráfico. Regime de competência
// (issue_date, total_value do lançamento por tipo de plano de contas) para bater
// com os cards e a DRE; a visão de caixa fica nas seções de Fluxo de Caixa.
export const loadMonthlyChartData = async (companyId, monthsCount = 6) => {
  const now = new Date();
  const startDate = formatLocalISO(new Date(now.getFullYear(), now.getMonth() - (monthsCount - 1), 1));
  const endDate = formatLocalISO(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  const { data, error } = await fetchAllRows(() =>
    supabase
      .from('FinancialEntry')
      .select('total_value, issue_date, AccountPlan(type)')
      .eq('company_id', companyId)
      .gte('issue_date', startDate)
      .lte('issue_date', endDate)
      .is('deleted_at', null)
      .order('id', { ascending: true })
  );

  if (error || !data) return [];

  return bucketEntriesByMonth(data, now, monthsCount);
};

// Despesas por mês nos últimos N meses até referenceDate (para o gráfico de linha
// do dashboard geral). Mesma base de competência do loadMonthlyChartData, mas com
// a data de referência acompanhando o período selecionado.
export const loadMonthlyDespesas = async (companyId, referenceDate, monthsCount = 6) => {
  if (!companyId || !referenceDate) return [];
  const startDate = formatLocalISO(
    new Date(referenceDate.getFullYear(), referenceDate.getMonth() - (monthsCount - 1), 1)
  );
  const endDate = formatLocalISO(
    new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0)
  );
  const { data, error } = await fetchAllRows(() =>
    supabase
      .from('FinancialEntry')
      .select('total_value, issue_date, AccountPlan(type)')
      .eq('company_id', companyId)
      .gte('issue_date', startDate)
      .lte('issue_date', endDate)
      .is('deleted_at', null)
      .order('id', { ascending: true })
  );
  if (error || !data) return [];
  return bucketEntriesByMonth(data, referenceDate, monthsCount);
};

// Resumo dos cards: por TIPO DO PLANO DE CONTAS, regime de competência
// (issue_date). Receitas = 'receita' (Receita Bruta da DRE); Despesas =
// deduções + custos + despesas (tudo que sai); Resultado = Receitas -
// Despesas (= Lucro Líquido da DRE).
export const loadEntriesSummary = async (companyId, range) => {
  const empty = { receitas: 0, despesas: 0, resultado: 0 };
  if (!range?.from || !range?.to) return empty;

  const entries = await loadDREData(companyId, range);
  let receita = 0;
  let saidas = 0;
  entries.forEach((e) => {
    const type = e.AccountPlan?.type;
    const value = Number(e.total_value) || 0;
    if (type === 'receita') receita += value;
    else if (isAccountPlanCompatible(type, ENTRY_TYPE.DESPESA)) saidas += value;
  });

  return {
    receitas: receita,
    despesas: saidas,
    resultado: receita - saidas,
  };
};

// Explode os lançamentos que têm breakdown (cobrança de OS) nas suas linhas por
// categoria, para a DRE separar por serviço. Cada linha vira um "pseudo-entry"
// com o AccountPlan e o valor da categoria, herdando data/documento da entry-mãe.
// Lançamentos sem breakdown passam intactos. Função pura (recebe o mapa
// financial_entry_id → linhas de breakdown).
export const explodeEntriesWithBreakdown = (entries, breakdownByEntry) => {
  const result = [];
  entries.forEach((entry) => {
    const rows = breakdownByEntry.get(entry.id);
    if (!rows || rows.length === 0) {
      result.push(entry);
      return;
    }
    rows.forEach((row) => {
      result.push({
        id: entry.id,
        total_value: row.value,
        issue_date: entry.issue_date,
        supplier_client: entry.supplier_client,
        document: entry.document,
        description: entry.description,
        AccountPlan: row.AccountPlan,
      });
    });
  });
  return result;
};

export const loadDREData = async (companyId, range) => {
  if (!range?.from || !range?.to) return [];

  const { data, error } = await fetchAllRows(() =>
    supabase
      .from('FinancialEntry')
      .select(
        'id, total_value, issue_date, supplier_client, document, description, order_service_id, AccountPlan(id, code, name, type, parent_id, level)'
      )
      .eq('company_id', companyId)
      .gte('issue_date', range.from)
      .lte('issue_date', range.to)
      .is('deleted_at', null)
      .order('id', { ascending: true })
  );

  if (error || !data) return [];

  // Cobranças de OS são 1 lançamento com breakdown por categoria — a DRE precisa
  // ver as categorias separadas. Só elas têm breakdown (order_service_id), então
  // buscamos o breakdown apenas dessas; as demais passam intactas.
  const osEntryIds = data.filter((e) => e.order_service_id).map((e) => e.id);
  const breakdown =
    osEntryIds.length > 0 ? await loadBreakdownByEntries(osEntryIds) : [];
  const byEntry = new Map();
  breakdown.forEach((row) => {
    const list = byEntry.get(row.financial_entry_id) || [];
    list.push(row);
    byEntry.set(row.financial_entry_id, list);
  });

  return explodeEntriesWithBreakdown(data, byEntry);
};

/**
 * Account plan types valid in DRE buckets.
 */
const VALID_TYPES = ['receita', 'deducao', 'custo', 'despesa'];

/**
 * Constrói a árvore da DRE: 4 buckets por type, cada um com grupos nível 1
 * expandidos recursivamente até as folhas. Os totais agregam para cima.
 *
 * @param {Array<{ total_value: number|string, AccountPlan: object|null }>} entries
 *   Lançamentos do período. Cada entry deve ter `total_value` e `AccountPlan`
 *   com `{ id, code, name, type, parent_id, level }`. Entries com type inválido
 *   ou AccountPlan ausente são ignoradas.
 *
 * @param {Array<{ id, code, name, type, parent_id, level }>} [allAccounts=[]]
 *   Lista completa de AccountPlans da empresa. Recomendado em produção: sem
 *   isso, se um período só tem entries em níveis profundos (e.g., só level-3),
 *   a árvore não consegue subir até o ancestral nível 1 e o nó raiz do bucket
 *   vira o ancestral mais profundo observado. Passar `allAccounts` resolve isso.
 *
 * @returns {{ receita: TreeNode[], deducao: TreeNode[], custo: TreeNode[], despesa: TreeNode[] }}
 *   Onde `TreeNode = { groupId, code, name, total, entries, children: TreeNode[] }`.
 *   Cada total agrega o próprio nó + filhos; `entries` são os lançamentos
 *   diretamente na conta do nó (por issue_date asc) — base da visão analítica.
 *   Sub-grupos sem entries no período são omitidos. Ordenado por `code` em
 *   cada nível.
 */
export const buildDRETree = (entries, allAccounts = []) => {
  // 1. Indexa todas as contas: primeiro o universo completo (allAccounts) para
  //    garantir que a cadeia de pais seja sempre acessível, depois as folhas
  //    presentes nos entries (caso alguma não esteja em allAccounts).
  const accountById = new Map();
  allAccounts.forEach((acc) => {
    if (acc && acc.id != null) accountById.set(acc.id, acc);
  });
  entries.forEach((e) => {
    const acc = e.AccountPlan;
    if (acc && acc.id != null && !accountById.has(acc.id)) {
      accountById.set(acc.id, acc);
    }
  });

  // 2. Para cada entry, descobre o ancestral nível 1 (sobe via parent_id)
  //    e a cadeia de pais até lá.
  const findChain = (account) => {
    const chain = [];
    let cur = account;
    while (cur) {
      chain.unshift(cur);
      if (cur.level === 1 || !cur.parent_id) break;
      const parent = accountById.get(cur.parent_id);
      if (!parent) break; // pai ausente — paramos aqui (cur vira a raiz)
      cur = parent;
    }
    return chain;
  };

  // 3. Acumula em uma estrutura de map aninhado por nível
  //    Cada nó: { groupId, code, name, total, children: Map<id, node> }
  const buckets = { receita: new Map(), deducao: new Map(), custo: new Map(), despesa: new Map() };

  const upsertNode = (parentMap, account) => {
    if (!parentMap.has(account.id)) {
      parentMap.set(account.id, {
        groupId: account.id,
        code: account.code || '',
        name: account.name || '',
        total: 0,
        entries: [],
        childrenMap: new Map(),
      });
    }
    return parentMap.get(account.id);
  };

  entries.forEach((entry) => {
    const account = entry.AccountPlan;
    if (!account || !VALID_TYPES.includes(account.type)) return;
    const value = Number(entry.total_value) || 0;
    const chain = findChain(account);
    let levelMap = buckets[account.type];
    let node = null;
    chain.forEach((acc) => {
      node = upsertNode(levelMap, acc);
      node.total += value;
      levelMap = node.childrenMap;
    });
    // O último nó da cadeia é a própria conta do lançamento.
    if (node) node.entries.push(entry);
  });

  // 4. Converte os Maps recursivamente em arrays ordenados por code
  const toSortedArray = (map) => {
    const arr = Array.from(map.values()).map((node) => ({
      groupId: node.groupId,
      code: node.code,
      name: node.name,
      total: node.total,
      entries: [...node.entries].sort((a, b) =>
        (a.issue_date || '').localeCompare(b.issue_date || '')
      ),
      children: toSortedArray(node.childrenMap),
    }));
    arr.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    return arr;
  };

  return {
    receita: toSortedArray(buckets.receita),
    deducao: toSortedArray(buckets.deducao),
    custo: toSortedArray(buckets.custo),
    despesa: toSortedArray(buckets.despesa),
  };
};

const stripAccents = (s) =>
  (s || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const flattenDRENodes = (nodes) => {
  const out = [];
  const walk = (list) => {
    (list || []).forEach((n) => {
      out.push(n);
      if (n.children) walk(n.children);
    });
  };
  walk(nodes);
  return out;
};

// Acha o total de um insumo na dedução: primeiro pelo código padrão, depois
// fallback por nome (sem acento). Robusto a plano de contas customizado.
const findInsumoTotal = (nodes, code, nameFragment) => {
  const byCode = nodes.find((n) => n.code === code);
  if (byCode) return byCode.total || 0;
  const frag = stripAccents(nameFragment);
  const byName = nodes.find((n) => stripAccents(n.name).includes(frag));
  return byName ? byName.total || 0 : 0;
};

// Extrai { funilaria, estetica } da árvore da DRE (grupo Deduções).
// Puro — recebe a árvore de buildDRETree.
export const extractInsumosFromDRE = (tree) => {
  const nodes = flattenDRENodes(tree?.deducao);
  return {
    funilaria: findInsumoTotal(nodes, '2.2', 'FUNILARIA'),
    estetica: findInsumoTotal(nodes, '2.3', 'ESTETICA'),
  };
};

// Carrega os insumos (Funilaria/Estética) da DRE no período.
export const loadInsumos = async (companyId, range) => {
  const empty = { funilaria: 0, estetica: 0 };
  if (!companyId || !range?.from || !range?.to) return empty;
  const [entries, accounts] = await Promise.all([
    loadDREData(companyId, range),
    loadAccountPlans(companyId),
  ]);
  const tree = buildDRETree(entries, accounts);
  return extractInsumosFromDRE(tree);
};

// Total de parcelas pendentes (a pagar e a receber). Aceita ranges separados
// porque o dashboard permite filtros independentes: "Próximos 2 meses" para
// Contas a Pagar e "Hoje" para Contas a Receber, por exemplo. O filtro é
// definido na tela e fica em sync com o modal correspondente.
export const loadDuePendingByRanges = async (companyId, payableRange, receivableRange) => {
  const empty = { aPagar: 0, aReceber: 0 };
  if (!payableRange?.from || !receivableRange?.from) return empty;

  const baseQuery = (range, type) =>
    supabase
      .from('Installment')
      .select('value, financial_entry:FinancialEntry!inner(type, company_id, deleted_at)')
      .eq('financial_entry.company_id', companyId)
      .eq('financial_entry.type', type)
      .eq('status', INSTALLMENT_STATUS.PENDENTE)
      .gte('due_date', range.from)
      .lte('due_date', range.to)
      .is('deleted_at', null)
      .is('financial_entry.deleted_at', null)
      .order('id', { ascending: true });

  const [payable, receivable] = await Promise.all([
    fetchAllRows(() => baseQuery(payableRange, ENTRY_TYPE.DESPESA)),
    fetchAllRows(() => baseQuery(receivableRange, ENTRY_TYPE.RECEITA)),
  ]);

  const aPagar = (payable.data || []).reduce((sum, i) => sum + (Number(i.value) || 0), 0);
  const aReceber = (receivable.data || []).reduce((sum, i) => sum + (Number(i.value) || 0), 0);
  return { aPagar, aReceber };
};
